import { DetailsPanel, LoadingIcon } from '@/components/controls';
import { useNichePersonImage, useSubject } from '@/hooks';
import { toProperCase } from '@/utils';
import {
  ElectricBolt as ElectricBoltIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardHeader,
  CardMedia,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import {
  DesktopClassic as DesktopClassicIcon,
  Pistol as PistolIcon,
} from 'mdi-material-ui';
import { FlagAvatar } from './FlagAvatar';

export function PrisonRecall({ recall, onOptionClick }) {
  const subjectType = 'PRISON_RECALL';
  const personId = recall?.subject?.id;
  const occurrenceId = recall?.occurrence?.id;
  const {
    query: {
      data: imageSrc,
      isLoading: imageLoading,
      isFetching: imageFetching,
    },
  } = useNichePersonImage(personId);
  const {
    query: {
      data: subject,
      isLoading: subjectLoading,
      isFetching: subjectFetching,
    },
  } = useSubject(subjectType, personId, occurrenceId);

  return (
    <Card variant="outlined" sx={{ minWidth: 240 }}>
      <CardHeader
        avatar={<FlagAvatar code="PRC" />}
        title={toProperCase(
          [recall?.subject?.forenames, recall?.subject?.surname]
            .filter(Boolean)
            .join(' '),
        )}
        action={
          <LoadingIcon
            isLoading={
              subjectLoading || subjectFetching || imageLoading || imageFetching
            }
          />
        }
      />
      {imageSrc ? (
        <CardMedia
          component="img"
          sx={{ minHeight: 240, width: '100%' }}
          image={imageSrc}
          alt="Photo of recall subject"
        />
      ) : (
        <Box
          sx={(theme) => ({
            height: 240,
            width: '100%',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300',
          })}
        />
      )}
      <Stack spacing={1} sx={{ p: 1 }}>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            variant="body3"
            color="textSecondary"
            sx={{ flexGrow: 1 }}
          >
            {recall?.occurrence?.number}
          </Typography>
        </Stack>
        {recall?.outstandingForOtherMatters && (
          <Stack direction="row" spacing={1}>
            <Chip label="Outstanding for other matters" size="small" />
          </Stack>
        )}
        <DetailsPanel
          entries={[
            {
              label: 'Last log entry',
              value: recall?.occurrence?.lastLogEntryTime
                ? format(recall.occurrence.lastLogEntryTime, 'dd/MM/yyyy HH:mm')
                : '',
            },
          ]}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
        >
          <Tooltip
            title={
              subject?.pncCirculated ? (
                <Stack>
                  <Typography sx={{ fontSize: 11 }}>
                    Circulated on PNC
                  </Typography>
                  <Typography sx={{ fontSize: 11 }}>
                    {subject.pncCirculated.username}
                  </Typography>
                  <Typography sx={{ fontSize: 11 }}>
                    {format(subject.pncCirculated.time, 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
              ) : (
                'Not circulated on PNC'
              )
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                personId,
                occurrenceId,
                subject?.code,
                'pncCirculated',
              )}
              color={subject?.pncCirculated ? 'primary' : 'default'}
            >
              <DesktopClassicIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.socialMediaCirculated ? (
                <Stack>
                  <Typography sx={{ fontSize: 11 }}>
                    Circulated on social media
                  </Typography>
                  <Typography sx={{ fontSize: 11 }}>
                    {subject.socialMediaCirculated.username}
                  </Typography>
                  <Typography sx={{ fontSize: 11 }}>
                    {format(
                      subject.socialMediaCirculated.time,
                      'dd/MM/yyyy HH:mm',
                    )}
                  </Typography>
                </Stack>
              ) : (
                'Not circulated on social media'
              )
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                personId,
                occurrenceId,
                subject?.code,
                'socialMediaCirculated',
              )}
              color={subject?.socialMediaCirculated ? 'primary' : 'default'}
            >
              <FacebookIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.taser ? (
                <Stack>
                  <Typography sx={{ fontSize: 11 }}>
                    Taser officers only
                  </Typography>
                  <Typography sx={{ fontSize: 11 }}>
                    {subject.taser.username}
                  </Typography>
                  <Typography sx={{ fontSize: 11 }}>
                    {format(subject.taser.time, 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
              ) : (
                'No Taser authority'
              )
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                personId,
                occurrenceId,
                subject?.code,
                'taser',
              )}
              color={subject?.taser ? 'primary' : 'default'}
            >
              <ElectricBoltIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.firearms ? (
                <Stack>
                  <Typography sx={{ fontSize: 11 }}>
                    Firearms officers only
                  </Typography>
                  <Typography sx={{ fontSize: 11 }}>
                    {subject.firearms.username}
                  </Typography>
                  <Typography sx={{ fontSize: 11 }}>
                    {format(subject.firearms.time, 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
              ) : (
                'No Firearms authority'
              )
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                personId,
                occurrenceId,
                subject?.code,
                'firearms',
              )}
              color={subject?.firearms ? 'primary' : 'default'}
            >
              <PistolIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}
